import { useState, useEffect } from "react";
import { Spinner, Alert, Form, FormLabel } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import { StyledForm } from "../../GlobalStyle";
import Button from "../Button/Button";
import styled from "styled-components";
import { useToast } from "src/context/toast/toast.provider";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import { useAccount } from "src/context/account/account.provider";

/**
 * Instructions for Using DynamicGoogleFormSubmitter Component:
 *
 * 1. Add the `DynamicGoogleFormSubmitter` component to your React project.
 * 2. Pass the following props to the component:
 *    - `formId` (string): The Google Apps Script deployment ID for the form submission endpoint.
 *    - `promoSourceRef` (string, optional): A reference string to track the promo source.
 *    - `formFields` (array): An array of objects defining the form fields. Each object should have:
 *        - `id` (string): Unique identifier for the field.
 *        - `label` (string): Translation key for the field label.
 *        - `type` (string, optional): Input type (e.g., "text", "email"). Defaults to "text".
 *        - `placeholder` (string, optional): Translation key for the placeholder text.
 *        - `required` (boolean, optional): Whether the field is required.
 *    - `formTextKey` (string, optional): Translation namespace for custom form text.
 *
 * 3. Ensure the `formId` corresponds to a Google Apps Script deployment that handles form submissions.
 *
 * Example Google Apps Script:
 *
 * ```javascript
 * function doPost(e) {
 *   const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
 *   const params = e.parameter;
 *
 *   const row = [
 *     new Date(),
 *     params.Language?.trim() || "",
 *     params.FirstName?.trim() || "",
 *     params.LastName?.trim() || "",
 *     params.Email?.trim() || "",
 *     params.PhoneNumber?.trim() || "",
 *     params.PlayerID?.trim() || "",
 *     params.FatherNominee?.trim() || ""
 *   ];
 *
 *   sheet.appendRow(row);
 *
 *   return ContentService.createTextOutput("Success");
 * }
 * ```
 *
 * 4. Attach the above Apps Script to a Google Sheet to store form submissions.
 * 5. Deploy the Apps Script as a web app and use the deployment ID as the `formId` prop.
 * 6. Customize the translations for form text in your i18n configuration, if needed.
 */

const DynamicGoogleFormSubmitter = ({
  formId,
  promoSourceRef,
  formFields = [],
  formTextKey,
  titles = true,
  sheetName,
  // Optional: externally controlled values that should be merged into the form
  // e.g., { Prize: 25, Email: "user@example.com" }
  // values,
}) => {
  const { t, i18n } = useTranslation();
  const { showToastFn } = useToast();
  const { orderIdFn, orderId } = useAccount();
  const [promoSource, setPromoSource] = useState(promoSourceRef);
  const [fieldValues, setFieldValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Helper function to get text from custom form key or dynamicGoogleForm namespace or default
  const getFormText = (key) => {

    // 1. Try to get from dynamicGoogleForm namespace in the same locale file
    if (formTextKey) {
      const dynamicText = t(`${formTextKey}.dynamicGoogleForm.${key}`, { defaultValue: null });
      if (dynamicText !== null) return dynamicText;
    }

    // 2. Fall back to default form texts from dynamicGoogleFormDefault.json
    return t(`dynamicGoogleFormDefault.${key}`);
  };

  // Helper function to get nested text (for success/error messages)
  const getNestedFormText = (parent, child) => {

    // 1. Try to get from dynamicGoogleForm namespace in the same locale file
    if (formTextKey) {
      const dynamicText = t(`${formTextKey}.dynamicGoogleForm.${parent}.${child}`, { defaultValue: null });
      if (dynamicText !== null) return dynamicText;
    }

    // 2. Fall back to default form texts from dynamicGoogleFormDefault.json
    return t(`dynamicGoogleFormDefault.${parent}.${child}`);
  };

  // Merge in externally provided values whenever they change
  // useEffect(() => {
  //   if (!values) return;
  //   setFieldValues((prev) => ({ ...prev, ...values }));
  // }, [JSON.stringify(values)]);

  const handleFieldChange = (fieldId, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const formUrl = `https://script.google.com/macros/s/${formId}/exec`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const formData = new FormData();
    formData.append("Language", i18n.language);
    formData.append("SheetName", sheetName);

    // Dynamically append all field values
    Object.entries(fieldValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add promo source if provided
    if (promoSource) {
      formData.append("PromoSource", promoSource);
    }

    try {
      const response = await fetch(formUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        showToastFn({
          show: true,
          title: getNestedFormText("error", "title"),
          message: getNestedFormText("error", "text"),
          autohide: false,
          variant: "danger",
        });
        return;
      }

      showToastFn({
        show: true,
        title: getNestedFormText("success", "title"),
        message: getNestedFormText("success", "text"),
        autohide: true,
        variant: "success",
      });

      // If we have firstName, lastName, and phone in our field values, pass them to orderIdFn
      if (fieldValues.FirstName && fieldValues.LastName && fieldValues.PhoneNumber) {
        orderIdFn({
          firstName: fieldValues.FirstName,
          lastName: fieldValues.LastName,
          phone: fieldValues.PhoneNumber,
        });
      }

      setIsSuccess(true);
    } catch (error) {
      setErrorMessage("Submission failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer>
      {isSuccess && (
        <SuccessOverlay>
          <ResponsiveImage src="svg/Check.svg" alt="check-icon" />
          <h2>{getNestedFormText("success", "title")}</h2>
          <p>{getNestedFormText("success", "text")}</p>
        </SuccessOverlay>
      )}
      <StyledForm onSubmit={handleSubmit} className="form-container">
        {titles && (
          <SubTitleSection
            subTitle={getFormText("enterNow")}
            as={"h1"}
            text={getFormText("qualify")}
          />
        )}

        {formFields.map((field) => (
          <Form.Group key={field.id} controlId={`form${field.id}`}>
            <FormLabel>{getFormText(field.label)}</FormLabel>
            <Form.Control
              type={field.type || "text"}
              name={field.id.toLowerCase()}
              value={fieldValues[field.id] || ""}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={getFormText(field.placeholder || field.label)}
              required={field.required}
            />
          </Form.Group>
        ))}

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Button
          type="submit"
          disabled={isLoading}
          bgColor="var(--red)"
          color="var(--white)"
          hoverColor="var(--black)"
          hoverBgColor="var(--white)"
          fluid
          label={
            isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              getFormText("submit")
            )
          }
        />
      </StyledForm>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  position: relative;
`;

const SuccessOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 3rem 4rem;
  z-index: 3;
  img {
    max-width: 3rem;
    margin: 0.5rem;
  }
`;

export default DynamicGoogleFormSubmitter;
